import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  TrendingUp,
  CalendarCheck,
  PoundSterling,
  MapPin,
  Users,
} from "lucide-react";
import confetti from "canvas-confetti";
import { SEOHead } from "@/components/SEOHead";

export default function Usa() {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const timeOptions = [];
  for (let hour = 8; hour <= 19; hour++) {
    for (const min of ["00", "15", "30", "45"]) {
      if (hour === 19 && min !== "00") continue;
      const isPM = hour >= 12;
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      const ampm = isPM ? "PM" : "AM";
      timeOptions.push({
        value: `${hour.toString().padStart(2, "0")}:${min}`,
        label: `${displayHour}:${min} ${ampm}`,
      });
    }
  }

  const getMinDate = () => {
    const minDate = new Date();
    minDate.setDate(minDate.getDate() + 7);
    return minDate.toISOString().split("T")[0];
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const data = Object.fromEntries(formData.entries());

    const trackingPayload = {
      type: "external_form_submission",
      timestamp: Date.now(),
      formId: "US Market Research Form",
      formData: {
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        phone: data.phone,
        organization: data.companyName,
        city: data.city,
        state: data.state,
        "contact.business_structure": data.businessStructure,
        "contact.number_of_employees": data.employees,
        "contact.preferred_date__time": `${data.preferredDate} ${data.preferredTime}`,
        "contact.has_whatsapp": data.hasWhatsapp,
      },
      formLabels: {
        first_name: "First Name",
        last_name: "Last Name",
        email: "Email Address",
        phone: "Phone Number",
        organization: "Business Name",
        city: "City",
        state: "State",
        "contact.business_structure": "Business Structure",
        "contact.number_of_employees": "Number of Employees",
        "contact.preferred_date__time": "Preferred Date & Time",
        "contact.has_whatsapp": "Has WhatsApp",
      },
      url: window.location.href,
      title: document.title,
      path: window.location.pathname,
      userAgent: navigator.userAgent,
      trackingId: "tk_4228c869be4b43a6af25f3931923e54d",
      locationId: "nsp8u9BgTJB40BULOWfw",
      sessionId: crypto.randomUUID(),
      properties: {
        deviceType: /Mobile|Android|iPhone/i.test(navigator.userAgent)
          ? "mobile"
          : "desktop",
        tags: ["usa"],
      },
    };

    fetch("https://backend.leadconnectorhq.com/external-tracking/events", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        version: "2021-07-28",
      },
      body: JSON.stringify(trackingPayload),
    }).catch(() => {});

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ["#004d8c", "#fe5240", "#ffffff"],
      });
    }, 1000);
  };

  return (
    <>
      <SEOHead
        title="US Window Cleaning Market Research | Clean Marketing"
        description="Clean Marketing is expanding to the USA. We're speaking with 3-4 ambitious US window cleaning business owners to learn about the market. Book a paid chat — no sales pitch."
        canonical="/usa"
      />
      <div className="min-h-screen bg-slate-50 font-sans text-foreground flex flex-col">
        {/* Simplified Header */}
        <header className="w-full py-6 bg-white border-b border-slate-200">
          <div className="container mx-auto px-4 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <img
                src="https://vibe.filesafe.space/1775166810156919871/attachments/07b67583-88fd-417b-bb44-aeaaee1d2dda.png"
                alt="Clean Marketing Logo"
                className="h-10"
              />
            </div>
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-slate-600 hover:text-primary transition-colors flex items-center"
            >
              View our UK site <ArrowRight className="ml-1 w-4 h-4" />
            </a>
          </div>
        </header>

        <main className="flex-grow">
          {/* Hero Section */}
          <section className="py-20 bg-secondary text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-secondary/50 rounded-3xl transform rotate-3 opacity-20 blur-xl"></div>
            <div className="container mx-auto px-4 max-w-5xl relative z-10">
              <div className="text-center max-w-3xl mx-auto">
                <div className="inline-flex items-center rounded-full border border-white/20 bg-white/10 px-3 py-1 text-sm font-medium text-white mb-6 backdrop-blur-sm">
                  <MapPin className="w-4 h-4 mr-2 text-primary" /> US Market
                  Research
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight mb-6">
                  Help us understand the{" "}
                  <span className="text-primary">US Window Cleaning</span>{" "}
                  market.
                </h1>
                <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                  We are the UK's leading marketing agency built exclusively for
                  window cleaners. We're looking to speak with 3-4 ambitious US
                  business owners to learn about your market.{" "}
                  <strong>No sales pitch. No catch.</strong>
                </p>
              </div>
            </div>
          </section>

          {/* Content & Form Section */}
          <section className="py-16 -mt-10 relative z-20">
            <div className="container mx-auto px-4 max-w-6xl">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* Left Column: Context & Stats */}
                <div className="space-y-8 bg-white p-8 rounded-2xl shadow-xl border border-slate-200">
                  <div>
                    <h2 className="text-2xl font-bold text-secondary mb-4">
                      Why are we doing this?
                    </h2>
                    <p className="text-slate-600 leading-relaxed mb-4">
                      Clean Marketing was founded by James Savory. I'm a former
                      window cleaner, but only gave up my business in December
                      2025, so hardly a former window cleaner at this point.
                      Over the last few years, we've built a predictable growth
                      engine that has transformed businesses across the UK and
                      Ireland.{" "}
                      <strong>
                        We are planning to open our marketing services to window
                        cleaners across the USA in the next 6 months.
                      </strong>
                    </p>
                    <p className="text-slate-600 leading-relaxed mb-6">
                      To prepare, we are trying to learn about the market in the
                      USA. For example, how you operate as businesses, what
                      similarities there are across different states, pricing
                      models, business structures, cleaning frequencies, and
                      more.
                    </p>
                    <div className="bg-primary/5 border border-primary/20 p-4 rounded-xl mb-6">
                      <div>
                        <p className="text-sm font-medium text-secondary mb-1">
                          Compensation for your time
                        </p>
                        <p className="text-sm text-slate-600">
                          I know your time is valuable. I&apos;ll be
                          compensating you with a <strong>fixed $100</strong>{" "}
                          for the chat (you&apos;ll just need to send me an
                          invoice afterwards).
                        </p>
                      </div>
                    </div>
                    <p className="text-slate-600 leading-relaxed mb-6">
                      The call will last about an hour to an hour and a half. I
                      will also share some of my expertise around window
                      cleaning marketing as part of the call. Absolutely no
                      sales pitch. Just a genuine chat about your business to
                      learn about the US window cleaning market.
                    </p>
                  </div>

                  <div className="bg-slate-50 p-6 rounded-xl border border-slate-100">
                    <h3 className="text-lg font-bold text-secondary mb-4">
                      What we've achieved in the UK:
                    </h3>
                    <div className="space-y-4">
                      <div className="flex items-center">
                        <div className="bg-primary/10 p-2 rounded-lg text-primary mr-4">
                          <Users className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-bold text-secondary">
                            50+ Clients
                          </p>
                          <p className="text-sm text-slate-500">
                            Including the biggest residential window cleaner in
                            the UK
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <div className="bg-primary/10 p-2 rounded-lg text-primary mr-4">
                          <PoundSterling className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-bold text-secondary">
                            £2.5M+ Recurring Revenue
                          </p>
                          <p className="text-sm text-slate-500">
                            Added to our clients' businesses
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <div className="bg-primary/10 p-2 rounded-lg text-primary mr-4">
                          <CalendarCheck className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-bold text-secondary">
                            12,500+ Bookings
                          </p>
                          <p className="text-sm text-slate-500">
                            Fully automated scheduling
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <div className="bg-primary/10 p-2 rounded-lg text-primary mr-4">
                          <TrendingUp className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-bold text-secondary">
                            36,000+ Leads
                          </p>
                          <p className="text-sm text-slate-500">
                            Generated across the country
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column: Form */}
                <div>
                  <Card className="border-primary/20 shadow-xl relative overflow-hidden bg-white h-full">
                    <CardHeader className="pb-6 border-b border-slate-100 bg-slate-50">
                      <CardTitle className="text-2xl text-secondary">
                        Let's have a chat
                      </CardTitle>
                      <CardDescription className="text-base">
                        Fill out your details below and we'll reach out to
                        schedule a brief, informal call.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-6">
                      {isSubmitted ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center space-y-4">
                          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                            <CheckCircle2 className="w-8 h-8 text-primary" />
                          </div>
                          <h3 className="text-2xl font-bold text-secondary">
                            Thank You!
                          </h3>
                          <p className="text-slate-600 max-w-md">
                            Your details have been received. James will be in
                            touch very soon to arrange the chat.
                          </p>
                        </div>
                      ) : (
                        <form onSubmit={handleSubmit} className="space-y-5">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <div className="space-y-2">
                              <Label htmlFor="firstName">First Name</Label>
                              <Input
                                id="firstName"
                                name="firstName"
                                required
                                placeholder="John"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="lastName">Last Name</Label>
                              <Input
                                id="lastName"
                                name="lastName"
                                required
                                placeholder="Doe"
                              />
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="email">Email Address</Label>
                            <Input
                              id="email"
                              name="email"
                              type="email"
                              required
                              placeholder="john@example.com"
                            />
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <div className="space-y-2">
                              <Label htmlFor="phone">Phone Number</Label>
                              <Input
                                id="phone"
                                name="phone"
                                type="tel"
                                required
                                placeholder="(555) 123-4567"
                              />
                            </div>
                            <div className="space-y-2 flex flex-col justify-end h-full">
                              <Label htmlFor="hasWhatsapp">
                                Do you use WhatsApp?
                              </Label>
                              <select
                                id="hasWhatsapp"
                                name="hasWhatsapp"
                                required
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 mt-auto"
                              >
                                <option value="">Select...</option>
                                <option value="Yes">Yes</option>
                                <option value="No">No</option>
                              </select>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="companyName">Business Name</Label>
                            <Input
                              id="companyName"
                              name="companyName"
                              required
                              placeholder="Acme Window Cleaning"
                            />
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <div className="space-y-2">
                              <Label htmlFor="city">City</Label>
                              <Input
                                id="city"
                                name="city"
                                required
                                placeholder="Austin"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="state">State</Label>
                              <Input
                                id="state"
                                name="state"
                                required
                                placeholder="TX"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <div className="space-y-2 flex flex-col justify-end h-full">
                              <Label htmlFor="businessStructure">
                                Business Structure
                              </Label>
                              <select
                                id="businessStructure"
                                name="businessStructure"
                                required
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 mt-auto"
                              >
                                <option value="">Select structure...</option>
                                <option value="LLC">LLC</option>
                                <option value="Sole-Proprietor">
                                  Sole-Proprietor
                                </option>
                                <option value="Other">Other</option>
                              </select>
                            </div>
                            <div className="space-y-2 flex flex-col justify-end h-full">
                              <Label htmlFor="employees">
                                Number of Employees (including yourself)
                              </Label>
                              <Input
                                id="employees"
                                name="employees"
                                type="number"
                                min="0"
                                required
                                placeholder="0"
                                className="mt-auto"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <div className="space-y-2">
                              <Label htmlFor="preferredDate">
                                Preferred Date for Chat
                              </Label>
                              <Input
                                id="preferredDate"
                                name="preferredDate"
                                type="date"
                                min={getMinDate()}
                                required
                                onClick={(e) => {
                                  try {
                                    if (
                                      "showPicker" in HTMLInputElement.prototype
                                    ) {
                                      e.currentTarget.showPicker();
                                    }
                                  } catch (err) {}
                                }}
                                className="cursor-pointer"
                              />
                            </div>
                            <div className="space-y-2 flex flex-col justify-end h-full">
                              <Label htmlFor="preferredTime">
                                Preferred Time (Your Local Time)
                              </Label>
                              <select
                                id="preferredTime"
                                name="preferredTime"
                                required
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 mt-auto cursor-pointer"
                              >
                                <option value="">Select time...</option>
                                {timeOptions.map((time) => (
                                  <option key={time.value} value={time.value}>
                                    {time.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            We will reach out to confirm if this time is
                            suitable. The call will be held via Zoom.
                          </p>

                          <Button
                            type="submit"
                            className="w-full h-12 text-base bg-primary text-primary-foreground hover:bg-[#e04825] transition-colors mt-4 shadow-md"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? "Sending..." : "I'm open to a chat"}
                          </Button>

                          <p className="text-xs text-center text-slate-500 mt-4">
                            By submitting this form, you agree to let us contact
                            you regarding this market research. We will never
                            sell your data.
                          </p>
                        </form>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </section>

          {/* About / Mission Section */}
          <section className="py-24 bg-secondary text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/50 rounded-3xl transform -rotate-3 opacity-20 blur-xl"></div>
            <div className="container mx-auto px-4 max-w-5xl relative z-10">
              <div className="flex flex-col md:flex-row items-center gap-12">
                <div className="md:w-1/3">
                  <img
                    src="https://vibe.filesafe.space/1775166810156919871/assets/188e792a-ab1b-4005-94d9-4e25f5a63282.jpg"
                    alt="Founder of Clean Marketing"
                    className="w-full max-w-[300px] mx-auto rounded-2xl shadow-2xl border-4 border-white/10"
                  />
                </div>
                <div className="md:w-2/3 text-left">
                  <div className="inline-flex items-center rounded-full border border-white/20 bg-white/10 px-3 py-1 text-sm font-medium text-white mb-6 backdrop-blur-sm">
                    <CheckCircle2 className="w-4 h-4 mr-2 text-primary" />{" "}
                    Supporting Local Business
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Built by James Savory
                  </h2>
                  <p className="text-lg text-blue-100 leading-relaxed mb-6">
                    I started Clean Marketing because I know exactly how hard it
                    is to run a local window cleaning business. I'm a former
                    window cleaner, but only gave up my business in December
                    2025, so hardly a former window cleaner at this point. I was
                    a small business owner on the tools, struggling to figure
                    out how to get consistent, high-quality quotes without
                    wasting hours knocking on doors or dropping flyers.
                  </p>
                  <p className="text-lg text-blue-100 leading-relaxed">
                    My mission is to truly support local businesses and help
                    them scale efficiently. We provide the exact marketing and
                    automation systems I built to grow my own business—from
                    targeted local ads to fully automated quoting and booking.
                    I'm passionate about helping small business owners because
                    I've been in your shoes.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Podcast Section */}
          <section className="py-24 bg-slate-900 text-white overflow-hidden">
            <div className="container mx-auto px-4 max-w-6xl">
              <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
                <div className="w-full lg:w-1/2 space-y-6 lg:pr-8">
                  <div className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
                    Featured On
                  </div>
                  <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                    Watch us on The Uncleanables Podcast
                  </h2>
                  <p className="text-lg text-slate-300">
                    We recently sat down with The Uncleanables - a podcast we
                    proudly sponsor and support - to discuss the exact marketing
                    strategies that are working right now for window cleaners
                    across the UK.
                  </p>
                  <div className="pt-4 flex flex-col sm:flex-row items-center sm:items-start gap-8">
                    <img
                      src="https://vibe.filesafe.space/1775166810156919871/attachments/9f531d29-517d-40d4-8af9-b2e1fc27ebfa.webp"
                      alt="The Uncleanables Podcast"
                      loading="lazy"
                      width="280"
                      height="176"
                      className="h-36 sm:h-44 max-w-[280px] object-contain"
                    />
                    <div className="flex flex-col justify-center gap-3">
                      <Button
                        className="bg-[#ddab1b] text-white hover:bg-[#08aed8] hover:text-white border-none transition-colors"
                        asChild
                      >
                        <a
                          href="https://www.theuncleanables.co.uk"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Visit The Uncleanables{" "}
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </a>
                      </Button>
                      <div className="flex flex-col gap-2 mt-2">
                        <p className="text-sm text-slate-400">
                          Join their community for support:
                        </p>
                        <Button
                          className="bg-[#5865F2] hover:bg-[#4752C4] text-white transition-colors border-none"
                          asChild
                        >
                          <a
                            href="https://www.theuncleanables.co.uk/join"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <svg
                              role="img"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                              className="w-5 h-5 mr-2 fill-current"
                            >
                              <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
                            </svg>
                            Join the Discord
                          </a>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="w-full lg:w-1/2">
                  <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl border border-slate-700 bg-slate-800">
                    <iframe
                      className="absolute inset-0 w-full h-full"
                      src="https://www.youtube.com/embed/Fjt-nrmVbtU?si=XDrBT8rOYa8dn0zb"
                      title="YouTube video player"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                      loading="lazy"
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <footer className="py-8 bg-slate-900 text-slate-400 text-center text-sm">
          <p>
            © {new Date().getFullYear()} Clean Marketing. All rights reserved.
          </p>
        </footer>
      </div>
    </>
  );
}
